import { hexToRgba } from "./sq-color-hex";
import type { SqColorMode, SqColorName, SqSoftColorName } from "./sq-colors";

const SQ_BADGE_SURFACE_CLASS: Record<SqColorName, string> = {
	red: "sq-badge-surface-red",
	blue: "sq-badge-surface-blue",
	green: "sq-badge-surface-green",
	amber: "sq-badge-surface-amber",
	gold: "sq-badge-surface-gold",
	purple: "sq-badge-surface-purple",
	orange: "sq-badge-surface-orange",
	bronze: "sq-badge-surface-bronze",
	silver: "sq-badge-surface-silver",
	slate: "sq-badge-surface-slate",
	grey: "sq-badge-surface-grey",
	indigo: "sq-badge-surface-indigo",
};

const SQ_SOFT_BADGE_CLASS: Record<SqSoftColorName, string> = {
	green_soft: "sq-soft-badge-green",
	orange_soft: "sq-soft-badge-orange",
	grey_soft: "sq-soft-badge-grey",
	indigo_soft: "sq-soft-badge-indigo",
};

const SQ_BAR_STRIPE_CLASS: Record<SqColorName, string> = {
	red: "sq-bar-stripe-red",
	blue: "sq-bar-stripe-blue",
	green: "sq-bar-stripe-green",
	amber: "sq-bar-stripe-amber",
	gold: "sq-bar-stripe-gold",
	purple: "sq-bar-stripe-purple",
	orange: "sq-bar-stripe-orange",
	bronze: "sq-bar-stripe-bronze",
	silver: "sq-bar-stripe-silver",
	slate: "sq-bar-stripe-slate",
	grey: "sq-bar-stripe-grey",
	indigo: "sq-bar-stripe-indigo",
};

/** Literal utilities — Tailwind only emits classes that appear as full strings in source. */
const SQ_SOLID_FILL_CLASS: Record<SqColorName, string> = {
	red: "bg-[var(--sq-red-static-from)]",
	blue: "bg-[var(--sq-blue-static-from)]",
	green: "bg-[var(--sq-green-static-from)]",
	amber: "bg-[var(--sq-amber-static-from)]",
	gold: "bg-[var(--sq-gold-static-from)]",
	purple: "bg-[var(--sq-purple-static-from)]",
	orange: "bg-[var(--sq-orange-static-from)]",
	bronze: "bg-[var(--sq-bronze-static-from)]",
	silver: "bg-[var(--sq-silver-static-from)]",
	slate: "bg-[var(--sq-slate-static-from)]",
	grey: "bg-[var(--sq-grey-static-from)]",
	indigo: "bg-[var(--sq-indigo-static-from)]",
};

const SQ_TEXT_FILL_FROM_CLASS: Record<SqColorName, string> = {
	red: "text-[var(--sq-red-static-from)]",
	blue: "text-[var(--sq-blue-static-from)]",
	green: "text-[var(--sq-green-static-from)]",
	amber: "text-[var(--sq-amber-static-from)]",
	gold: "text-[var(--sq-gold-static-from)]",
	purple: "text-[var(--sq-purple-static-from)]",
	orange: "text-[var(--sq-orange-static-from)]",
	bronze: "text-[var(--sq-bronze-static-from)]",
	silver: "text-[var(--sq-silver-static-from)]",
	slate: "text-[var(--sq-slate-static-from)]",
	grey: "text-[var(--sq-grey-static-from)]",
	indigo: "text-[var(--sq-indigo-static-from)]",
};

const SQ_TEXT_FILL_TO_CLASS: Record<SqColorName, string> = {
	red: "text-[var(--sq-red-static-to)]",
	blue: "text-[var(--sq-blue-static-to)]",
	green: "text-[var(--sq-green-static-to)]",
	amber: "text-[var(--sq-amber-static-to)]",
	gold: "text-[var(--sq-gold-static-to)]",
	purple: "text-[var(--sq-purple-static-to)]",
	orange: "text-[var(--sq-orange-static-to)]",
	bronze: "text-[var(--sq-bronze-static-to)]",
	silver: "text-[var(--sq-silver-static-to)]",
	slate: "text-[var(--sq-slate-static-to)]",
	grey: "text-[var(--sq-grey-static-to)]",
	indigo: "text-[var(--sq-indigo-static-to)]",
};

const SQ_LINEAR_GRADIENT_135_CLASS: Record<SqColorName, string> = {
	red: "bg-[image:linear-gradient(135deg,var(--sq-red-static-from),var(--sq-red-static-to))]",
	blue: "bg-[image:linear-gradient(135deg,var(--sq-blue-static-from),var(--sq-blue-static-to))]",
	green: "bg-[image:linear-gradient(135deg,var(--sq-green-static-from),var(--sq-green-static-to))]",
	amber: "bg-[image:linear-gradient(135deg,var(--sq-amber-static-from),var(--sq-amber-static-to))]",
	gold: "bg-[image:linear-gradient(135deg,var(--sq-gold-static-from),var(--sq-gold-static-to))]",
	purple: "bg-[image:linear-gradient(135deg,var(--sq-purple-static-from),var(--sq-purple-static-to))]",
	orange: "bg-[image:linear-gradient(135deg,var(--sq-orange-static-from),var(--sq-orange-static-to))]",
	bronze: "bg-[image:linear-gradient(135deg,var(--sq-bronze-static-from),var(--sq-bronze-static-to))]",
	silver: "bg-[image:linear-gradient(135deg,var(--sq-silver-static-from),var(--sq-silver-static-to))]",
	slate: "bg-[image:linear-gradient(135deg,var(--sq-slate-static-from),var(--sq-slate-static-to))]",
	grey: "bg-[image:linear-gradient(135deg,var(--sq-grey-static-from),var(--sq-grey-static-to))]",
	indigo: "bg-[image:linear-gradient(135deg,var(--sq-indigo-static-from),var(--sq-indigo-static-to))]",
};

/** Diagonal bar stripe (stat bars) — uses static pair in both UI themes. */
export function sqBarStripeClass(color: SqColorName): string {
	return `rounded-full ${SQ_BAR_STRIPE_CLASS[color]}`;
}

/** Simple two-stop gradient without stripe overlay. */
export function sqLinearGradientClass(
	color: SqColorName,
	_mode: SqColorMode = "static",
	angle: "to bottom right" | "135deg" = "135deg",
): string {
	if (angle === "to bottom right") {
		return SQ_BADGE_SURFACE_CLASS[color];
	}
	return SQ_LINEAR_GRADIENT_135_CLASS[color];
}

/** Badge/ribbon surface: grey underlay + theme-aware gradient pairs (CSS utilities). */
export function sqBadgeGradientClass(color: SqColorName): string {
	return SQ_BADGE_SURFACE_CLASS[color];
}

/** Soft translucent status chip surface. */
export function sqSoftBadgeClass(color: SqSoftColorName): string {
	return SQ_SOFT_BADGE_CLASS[color];
}

/** Solid fill using the static pair's primary (`from`) stop. */
export function sqSolidFillClass(color: SqColorName): string {
	return SQ_SOLID_FILL_CLASS[color];
}

/** Text/stroke color from a static gradient stop. */
export function sqTextFillClass(
	color: SqColorName,
	stop: "from" | "to" = "from",
	_mode: SqColorMode = "static",
): string {
	return stop === "from"
		? SQ_TEXT_FILL_FROM_CLASS[color]
		: SQ_TEXT_FILL_TO_CLASS[color];
}

/** Shared badge label chrome (credit estimates, battle slots, SqStatBadge). */
export const SQ_BADGE_LABEL_CLASS =
	"font-black uppercase leading-tight tracking-wide text-white";

export { hexToRgba };
